import { useEffect, useState } from "react";
import { AsideAdminComponent } from "../layout/aside-admin";
import React from "react";
import { activeServiceId, pageIndicator } from "../apollo";
import { useReactiveVar } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import exit from "../assets/icon/exit.png";
import {
  PageType,
  ComponentTabLink,
  ITabTitle,
} from "../components/component-tab";
import { ITabLink } from "../components/component-tab";
import { Modal } from "@mui/material";
import { TableError } from "../components/table-error";
import { PaginationComponent } from "../components/pagination";
import { useDelOrder } from "../hooks/useDelOrder";
import { useListOrder } from "../hooks/useListOrder";
import { useEditOrdersStatusWithQuantity } from "../hooks/useEditOrdersStatusWithQuantity";
import { useCancelOrders } from "../hooks/useCancelOrders";
import { useOrderStatusChangedSubscription } from "../hooks/subOrderStatusChanged";
import moment from "moment";
import {
  DelOrderInput,
  DelOrderMutation,
  EditOrdersStatusWithQuantityInput,
  ListOrderInput,
  ListOrderQuery,
  OrderStatus,
} from "../generated/graphql";
import { ListOrder } from "./admin-list-order";

// 26년 5월 이전 주문내역은 주문관리 전체 목록에 노출되지 않도록 하한선을 둔다.
const MIN_START_DATE = new Date("2026-05-01T00:00:00.000Z");

// 배송관리 페이지들(주문확인/배송/배송완료 탭 등)의 상태 버튼과 동일하게,
// 긴 라벨은 단어 사이에 공백을 넣어 w-[48px] 버튼 안에서 두 줄로 자연스럽게 줄바꿈되도록 한다.
const OrderStatusLabel: Record<OrderStatus, string> = {
  [OrderStatus.Inspect]: "주문 확인",
  [OrderStatus.Bound]: "출고 대기",
  [OrderStatus.Pending]: "배송 준비중",
  [OrderStatus.HandOut]: "배송중",
  [OrderStatus.Drone]: "드론 비행",
  [OrderStatus.Port]: "최종 배송준비",
  [OrderStatus.Stored]: "픽업포트",
  [OrderStatus.PendingDelivered]: "최종 배송",
  [OrderStatus.Delivered]: "배송 완료",
  [OrderStatus.Refunded]: "환불",
  [OrderStatus.Exchange]: "교환",
  [OrderStatus.Complete]: "배송 확정",
  [OrderStatus.Canceled]: "취소",
  [OrderStatus.PendingRefund]: "환불 대기",
};

// 상품관리 페이지의 상태 버튼과 동일한 배색 규칙(진행중=남색 채움 / 완료·확정=회색 채움 / 환불·교환·취소=빨간 테두리)
const OrderStatusButtonClass = (status: OrderStatus) => {
  if (status === OrderStatus.Delivered || status === OrderStatus.Complete) {
    return "bg-[#CCCCCC] text-white";
  }
  if (
    status === OrderStatus.Refunded ||
    status === OrderStatus.Exchange ||
    status === OrderStatus.PendingRefund
  ) {
    return "text-[#B42713] border border-[#B42713]";
  }
  if(status === OrderStatus.Canceled){
    return "bg-[#B42713] text-white";
  }else {
  return "bg-[#555B94] text-white";
  }
};

// 상태 변경 모달: 배송관리(배송완료 탭 등)에서 쓰는 상태변경 모달과 동일한 구성/스타일을 쓰되,
// 주문관리는 모든 상태를 한 곳에서 다루므로 "주문접수" 그룹만 추가했다.
// (HandOut/PendingRefund는 기존 배송관리 어느 모달에서도 선택 옵션으로 쓰인 적이 없어 제외)
// "취소"는 다른 항목과 달리 단순 상태값 변경이 아니라, 실제 결제 취소(환불)까지 처리하는
// cancelOrder 뮤테이션을 호출한다 (아래 confirm 버튼 onClick 참고).
const STATUS_GROUPS: {
  group: string;
  items: { status: OrderStatus; label: string; hasQuantity?: boolean }[];
}[] = [
  {
    group: "주문접수",
    items: [{ status: OrderStatus.Inspect, label: "주문확인" }],
  },
  {
    group: "배송 준비",
    items: [{ status: OrderStatus.Pending, label: "배송 준비" }],
  },
  {
    group: "배송중",
    items: [{ status: OrderStatus.Drone, label: "드론 비행" }],
  },
  {
    group: "배송 완료",
    items: [{ status: OrderStatus.Delivered, label: "배송 완료" }],
  },
  {
    group: "환불",
    items: [
      { status: OrderStatus.Refunded, label: "환불", hasQuantity: true },
    ],
  },
  {
    group: "확정",
    items: [{ status: OrderStatus.Complete, label: "확정" }],
  },
  {
    group: "취소",
    items: [{ status: OrderStatus.Canceled, label: "취소" }],
  },
];

export const AdminListOrderAll = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const currentServiceId = useReactiveVar(activeServiceId);
  const [open, setOpen] = useState(false);
  const [stateIds, setStateIds] = useState<number[]>([]);
  const [change, setChange] = useState<OrderStatus>(OrderStatus.Inspect);
  const [quantity, setQuantity] = useState<number>(0);

  const word = "주문내역";

  // -- 주문관리 리스트 (상태 필터 없이 전체 조회, 26년 5월 이전 제외) --
  const onCompleted = async (data: ListOrderQuery) => {
    const {
      listOrder: { ok, err },
    } = data;
    if (ok) {
      await refetch();
    } else {
      alert(err);
    }
  };

  const [listOrder, { data, loading, refetch }] = useListOrder(onCompleted);

  // 다른 PC에서 이 페이지(또는 취소/상태변경을 발생시키는 다른 화면)를 통해 주문 상태가
  // 바뀌면 서버가 orderStatusChanged 이벤트를 보내온다. 그 신호를 받으면 지금 보고 있는
  // 주문 목록을 자동으로 refetch해서 최신 상태로 맞춘다.
  const { data: orderStatusChangedData } = useOrderStatusChangedSubscription();
  useEffect(() => {
    if (orderStatusChangedData) {
      refetch();
    }
  }, [orderStatusChangedData]);

  const listOrderHandler = (
    page: number | null,
    search: string | null,
    start?: Date | null,
    end?: Date | null
  ) => {
    const effectiveStart =
      start && new Date(start).getTime() > MIN_START_DATE.getTime()
        ? new Date(start)
        : MIN_START_DATE;
    const input: ListOrderInput = {
      ...(page && { page: +page }),
      ...(search && { search: decodeURI(search) }),
      start: effectiveStart,
      ...(end && { end }),
      ...(activeServiceId() && { serviceId: activeServiceId()! }),
    };
    listOrder({ variables: { input } });
  };

  // --- Search ---
  const setSearchHandler = (search: string) => {
    navigate({
      pathname: "/order-manage",
      search: `${search ? `?search=${search}` : ""}`,
    });
    listOrderHandler(1, search);
  };

  // --- Page ---
  const pageHandler = (page: number) => {
    const search =
      location.search.split("search=").length > 1
        ? location.search.split("search=")[1].split("&")[0]
        : null;
    setPage(page);
    navigate({
      pathname: "/order-manage",
      search: `?page=${page}${search ? `&search=${search}` : ""}`,
    });
    listOrderHandler(page, search);
  };

  // --- Period ---
  const periodHandler = (sDate?: Date, eDate?: Date) => {
    listOrderHandler(1, search, sDate, eDate);
  };

  useEffect(() => {
    pageIndicator("order");
    setPage(
      location.search.split("page=").length > 1
        ? +location.search.split("page=")[1].split("&")[0]
        : 1
    );
    setSearch(
      location.search.split("search=").length > 1
        ? location.search.split("search=")[1].split("&")[0]
        : ""
    );
    listOrderHandler(page, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, currentServiceId]);

  const tabList: ITabLink[] = [];
  const stitle: ITabTitle = { text: "주문관리" };

  // --- 상태 변경 ---
  // 한 영수증(주문번호)에 상품이 여러 개 담겨있어도 상태 버튼은 하나로 합쳐서 보여주므로,
  // 상태변경은 그 영수증에 속한 주문(상품) id 전체를 한 번에 받아서 순서대로 처리한다.
  const StateHandler = (ids: number[], currentStatus: OrderStatus) => {
    setStateIds(ids);
    setChange(currentStatus);
    setOpen(true);
  };

  // 여러 상품(주문)의 상태를 한 번에 바꿀 때도(같은 영수증을 합쳐서 보여주는 상태 버튼 등),
  // 배송중/배송완료 SMS는 영수증 단위로 묶어서 1건만 가도록 editOrdersStatusWithQuantity(복수)
  // 뮤테이션을 한 번만 호출한다(주문마다 개별 호출하던 방식은 취소 문자와 마찬가지로 문자가
  // 건마다 따로 나가는 문제가 있었음).
  const [
    editOrdersStatusWithQuantity,
    { loading: editOrderStatusLoading },
  ] = useEditOrdersStatusWithQuantity();
  const editOrderStatusWithQuantityHandler = async (
    orderIds: number[],
    status: OrderStatus,
    quantity?: number
  ) => {
    if (editOrderStatusLoading) return;
    const editOrdersStatusInput: EditOrdersStatusWithQuantityInput = {
      orderIds,
      status,
      ...((status === OrderStatus.Refunded ||
        status === OrderStatus.Exchange) && { quantity }),
    };
    const { data } = await editOrdersStatusWithQuantity({
      variables: { input: editOrdersStatusInput },
    });
    if (!data?.editOrdersStatusWithQuantity.ok) {
      alert(data?.editOrdersStatusWithQuantity.err);
    }
    await refetch();
  };

  // ----- 취소(환불) ------
  // 사용자가 주문내역에서 취소하면 결제가 실제로 환불되는 것과 동일하게,
  // 관리자 페이지에서 상태를 "취소"로 바꾸면 결제 취소(환불)까지 함께 처리한다.
  // 여러 건을 한 번에 취소할 때(같은 영수증의 상품 전체 등), 결제취소 자체는 주문마다
  // 개별 처리되지만(배송비 포함 여부 계산 때문에 백엔드에서 순서대로 처리함) 취소 SMS는
  // 영수증 단위로 묶어서 1건만 가도록 cancelOrders(복수) 뮤테이션을 한 번만 호출한다.
  const [cancelOrders, { loading: cancelOrdersLoading }] = useCancelOrders();
  const cancelOrderHandler = async (orderIds: number[]) => {
    if (cancelOrdersLoading) return;
    if (
      !window.confirm(
        "취소를 선택하면 결제가 실제로 취소(환불)됩니다. 되돌릴 수 없는데, 계속하시겠습니까?"
      )
    ) {
      return;
    }
    const { data } = await cancelOrders({ variables: { input: { orderIds } } });
    if (data?.cancelOrders.ok) {
      alert("취소(환불)되었습니다.");
    } else {
      alert(data?.cancelOrders.err ?? "취소 처리 중 오류가 발생했습니다.");
    }
    await refetch();
  };

  // ----- 삭제 ------
  const onDelCompleted = async (data: DelOrderMutation) => {
    const {
      delOrder: { ok, err },
    } = data;
    if (ok) {
      alert("주문내역 삭제");
      await refetch();
    } else {
      alert(err);
    }
  };
  // 같은 영수증(주문번호)에 속한 여러 상품 라인을 표에서 한 덩어리로 묶어서 보여주기 위한 그룹핑.
  // 주문번호/주문일시/배송비/총 결제금액/구매자/수취인/주소/연락처는 영수증 단위로 동일하므로
  // 그룹의 첫 행에서만 rowSpan으로 보여주고, 상태/상품명/상품옵션/수량/결제금액은 상품(주문)마다
  // 다를 수 있으므로(개별 취소 등) 그룹 안에서도 한 줄씩 그대로 보여준다.
  const orderRows = data?.listOrder.orders ?? [];
  const orderRowGroups: { receiptId?: number; orders: typeof orderRows }[] = [];
  orderRows.forEach((order) => {
    const receiptId = order.receipt?.id;
    const lastGroup = orderRowGroups[orderRowGroups.length - 1];
    if (lastGroup && receiptId !== undefined && lastGroup.receiptId === receiptId) {
      lastGroup.orders.push(order);
    } else {
      orderRowGroups.push({ receiptId, orders: [order] });
    }
  });

  const [delOrder, { loading: delOrderLoading }] = useDelOrder(onDelCompleted);
  const delOrderHandler = (orderId: number) => {
    if (delOrderLoading) return;
    if (
      window.confirm(
        "삭제한 주문내역은 복구할 수 없습니다. 정말로 삭제하시겠습니까?"
      )
    ) {
      const input: DelOrderInput = {
        orderId,
      };
      delOrder({ variables: { input } });
    }
  };

  return (
    <div className="bg-main bg-opacity-50 w-full h-full min-h-screen">
      <div className="w-full mx-auto h-full min-h-screen bg-gray-100">
        <main id="main">
          <div className="grid grid-cols-[200px_1fr] max-w-full">
            <AsideAdminComponent />
            <div className="mypages bg-gray-100 w-full min-w-[1200px] overflow-hidden">
              <div className="orderWrap">
                <div className="h-fix-content relative">
                  <div className={`w-full h-full px-10 py-8`}>
                    <ComponentTabLink
                      tabs={tabList}
                      stitle={stitle}
                      type={PageType.All}
                      setSearchs={setSearchHandler}
                      setDateSearch={periodHandler}
                      refetch={refetch}
                    />
                    {/* 목록 */}
                    <div className=" shadow-lg h-[800px] h-fix-content w-full overflow-auto bg-white">
                      <table className="align-center text-sm ">
                        <thead>
                          <tr className="border-b border-[#F9F8F9] h-8 break-all">
                            <th className="text-[#8B8C8C] min-w-[40px]">no</th>
                            <th className="text-[#8B8C8C] min-w-[64px]">
                              상태
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              주문번호
                            </th>
                            <th className="text-[#8B8C8C] min-w-[72px]">
                              주문일시
                            </th>
                            <th className="text-[#8B8C8C] min-w-[168px]">
                              상품명
                            </th>
                            <th className="text-[#8B8C8C] min-w-[168px]">
                              상품옵션
                            </th>
                            <th className="text-[#8B8C8C] min-w-[48px]">
                              수량
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              결제금액
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              배송비
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              총 결제금액
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              구매자
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              수취인
                            </th>
                            <th className="text-[#8B8C8C] min-w-[168px]">
                              주소
                            </th>


                            <th className="text-[#8B8C8C] min-w-[128px]">
                              연락처
                            </th>
                            <th className="text-[#8B8C8C] min-w-[88px]">
                              무게
                            </th>
                            <th className="text-[#8B8C8C] min-w-[80px]">
                              비고
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {!loading &&
                            orderRowGroups.map((group, groupIndex) =>
                              group.orders.map((order, indexInGroup) => (
                                <tr
                                  className={`h-[68px] ${
                                    indexInGroup === group.orders.length - 1
                                      ? "border-b-2 border-[#B5B5B5]"
                                      : "border-b border-dashed border-[#E0E0E0]"
                                  }`}
                                  key={`order-manage-${order.id}`}
                                >
                                  {indexInGroup === 0 && (
                                    <td
                                      className="table-text"
                                      rowSpan={group.orders.length}
                                    >
                                      {(page - 1) * 20 + groupIndex + 1}
                                    </td>
                                  )}
                                  {indexInGroup === 0 &&
                                    (() => {
                                      // 그룹(영수증) 안의 상품들이 전부 같은 상태면 그 상태를,
                                      // 상품별로 상태가 갈려있으면(개별 취소 등) "혼합"을 보여준다.
                                      const distinctStatuses = Array.from(
                                        new Set(
                                          group.orders.map((o) => o.status)
                                        )
                                      );
                                      const groupStatus =
                                        distinctStatuses.length === 1
                                          ? distinctStatuses[0]
                                          : undefined;
                                      const groupAllCanceled = group.orders.every(
                                        (o) => o.status === OrderStatus.Canceled
                                      );
                                      const groupIds = group.orders.map(
                                        (o) => o.id
                                      );
                                      return (
                                        <td
                                          className="table-text py-1"
                                          rowSpan={group.orders.length}
                                        >
                                          <button
                                            disabled={groupAllCanceled}
                                            className={`rounded-lg py-[4px] px-[3px] w-[48px] h-10 font-bold keep-all disabled:opacity-60 disabled:cursor-default ${
                                              groupStatus
                                                ? OrderStatusButtonClass(
                                                    groupStatus
                                                  )
                                                : "bg-[#CCCCCC] text-white"
                                            }`}
                                            onClick={() => {
                                              if (groupAllCanceled) return;
                                              StateHandler(
                                                groupIds,
                                                groupStatus ?? group.orders[0].status
                                              );
                                            }}
                                          >
                                            {groupStatus
                                              ? OrderStatusLabel[groupStatus]
                                              : "혼합"}
                                          </button>
                                        </td>
                                      );
                                    })()}
                                  {indexInGroup === 0 && (
                                    <td
                                      className="table-text break-all"
                                      rowSpan={group.orders.length}
                                    >
                                      {order.receipt?.receiptNum}
                                    </td>
                                  )}
                                  {indexInGroup === 0 && (
                                    <td
                                      className="table-text font-bold"
                                      rowSpan={group.orders.length}
                                    >
                                      {order.createdAt &&
                                        moment(order.createdAt).format(
                                          "YYMMDD"
                                        )}{" "}
                                      <br />
                                      {order.createdAt &&
                                        moment(order.createdAt).format(
                                          "H : mm"
                                        )}
                                    </td>
                                  )}
                                  <td className="table-text">
                                    {order.orderReceipt?.good_title}
                                  </td>
                                  <td className="table-text">
                                    {order.orderReceipt?.goodOption_name}
                                  </td>
                                  <td className="table-text font-bold">
                                    {order.orderReceipt?.order_quantity}
                                  </td>
                                  <td className="table-text">
                                    {order.orderReceipt?.order_amount}
                                  </td>
                                  {indexInGroup === 0 && (
                                    <td
                                      className="table-text"
                                      rowSpan={group.orders.length}
                                    >
                                      {order.receipt?.deliveryFee?.toLocaleString()}
                                    </td>
                                  )}
                                  {indexInGroup === 0 && (
                                    <td
                                      className="table-text font-bold"
                                      rowSpan={group.orders.length}
                                    >
                                      {order.receipt?.paid_amount?.toLocaleString()}
                                    </td>
                                  )}
                                  {indexInGroup === 0 && (
                                    <td
                                      className="table-text"
                                      rowSpan={group.orders.length}
                                    >
                                      {order.receipt?.user?.name}
                                    </td>
                                  )}
                                  {indexInGroup === 0 && (
                                    <td
                                      className="table-text font-bold"
                                      rowSpan={group.orders.length}
                                    >
                                      {order.receipt?.receiver
                                        ? order.receipt.receiver
                                        : order.receipt?.user?.name}
                                    </td>
                                  )}
                                  {indexInGroup === 0 && (
                                    <td
                                      className="table-text"
                                      rowSpan={group.orders.length}
                                    >
                                      {order.receipt?.address}
                                    </td>
                                  )}
                                  {indexInGroup === 0 && (
                                    <td
                                      className="table-text"
                                      rowSpan={group.orders.length}
                                    >
                                      {order.receipt?.phone
                                        ? order.receipt.phone
                                        : order.receipt?.user?.phone}
                                    </td>
                                  )}
                                  <td className="table-text">
                                    {order.good?.weight &&
                                      order.good?.weight + "g"}
                                  </td>

                                  <td className="table-text">
                                    {/* <button
                                      className="bg-[#CCCCCC] rounded-lg py-[6px]  w-[48px] h-7 text-white"
                                      onClick={() => delOrderHandler(order.id)}
                                    >
                                      삭제
                                    </button> */}
                                  </td>
                                </tr>
                              ))
                            )}
                        </tbody>
                      </table>

                      {loading && (
                        <TableError errorMessage={`정보를 불러오는중입니다`} />
                      )}
                      {!loading &&
                        data?.listOrder.orders &&
                        data.listOrder.orders.length < 1 && (
                          <TableError
                            errorMessage={`해당하는 ${word} 정보가 없습니다`}
                          />
                        )}

                      {!loading &&
                        data?.listOrder.orders &&
                        data.listOrder.orders.length > 0 && (
                          <PaginationComponent
                            page={page}
                            totalPages={Number(data.listOrder.totalPages)}
                            totalResults={Number(data.listOrder.totalResults)}
                            pageHandler={pageHandler}
                          />
                        )}

                      <Modal
                        open={open}
                        onClose={() => {
                          setOpen(false);
                        }}
                      >
                        <div className="max-w-[360px] w-full absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2   bg-[#F9F8F9] rounded-lg">
                          <div className="text-center py-2 h-10 text-white font-bold bg-[#3D3E49] rounded-t-lg  ">
                            <div className="flex flex-wrap justify-between">
                              <p className="w-[240px] ml-[40px] font-bold text-base">
                                주문 상태변경
                              </p>
                              <img
                                src={exit}
                                alt="닫기"
                                className="w-6 mr-2 cursor-pointer"
                                onClick={() => setOpen(false)}
                              ></img>
                            </div>
                          </div>
                          <div className="flex flex-wrap content-center">
                            <div className="px-10 py-10">
                              <div className="grid grid-rows-7 gap-y-4">
                                {STATUS_GROUPS.map(({ group, items }) => (
                                  <React.Fragment key={`status-group-${group}`}>
                                    <p className="font-bold">{group}</p>
                                    {items.map(({ status, label, hasQuantity }) => (
                                      <div key={`status-option-${status}`}>
                                        <input
                                          type="radio"
                                          name="order"
                                          id={`status-${status}`}
                                          checked={change === status}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setChange(status);
                                            }
                                          }}
                                        ></input>
                                        <span className="ml-4">{label}</span>
                                        {hasQuantity && (
                                          <div className="pt-2">
                                            <input
                                              type="text"
                                              className={`w-full`}
                                              disabled={change !== status}
                                              onChange={(e) =>
                                                setQuantity(
                                                  Number(e.target.value)
                                                )
                                              }
                                            ></input>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </React.Fragment>
                                ))}
                              </div>
                              {change === OrderStatus.Canceled && (
                                <p className="text-[#B42713] text-xs pt-4 leading-5">
                                  "취소"를 선택하고 확인을 누르면 결제가 실제로
                                  취소(환불)됩니다. 되돌릴 수 없으니 신중히
                                  선택해주세요.
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            className="w-full h-14 bg-[#F0F0F4] rounded-b-lg"
                            onClick={() => {
                              if (change === OrderStatus.Canceled) {
                                cancelOrderHandler(stateIds);
                              } else {
                                editOrderStatusWithQuantityHandler(
                                  stateIds,
                                  change,
                                  quantity
                                );
                              }
                              setOpen(false);
                            }}
                          >
                            확인
                          </button>
                        </div>
                      </Modal>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
